import { TriangleAlert } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmContext, type ConfirmRequest } from './confirm-context'

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [request, setRequest] = useState<ConfirmRequest | null>(null)
    const [typed, setTyped] = useState('')
    const resolveRef = useRef<((value: boolean) => void) | null>(null)

    const confirm = useCallback((next: ConfirmRequest) => {
        setTyped('')
        setRequest(next)
        return new Promise<boolean>(resolve => {
            resolveRef.current = resolve
        })
    }, [])

    const settle = useCallback((value: boolean) => {
        resolveRef.current?.(value)
        resolveRef.current = null
        setRequest(null)
    }, [])

    const value = useMemo(() => confirm, [confirm])
    const blocked = request?.requireTyping !== undefined && typed.trim() !== request.requireTyping

    return (
        <ConfirmContext value={value}>
            {children}

            <AlertDialog
                open={request !== null}
                // Dismissing by Escape or backdrop must resolve, not leave the caller
                // awaiting a promise that never settles.
                onOpenChange={open => {
                    if (!open) settle(false)
                }}>
                {request && (
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                                {request.tone === 'destructive' && (
                                    <TriangleAlert
                                        aria-hidden
                                        className="size-4 shrink-0 text-destructive-soft-foreground"
                                    />
                                )}
                                {request.title}
                            </AlertDialogTitle>
                            {request.description && (
                                <AlertDialogDescription>{request.description}</AlertDialogDescription>
                            )}
                        </AlertDialogHeader>

                        {request.requireTyping !== undefined && (
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="confirm-typing">
                                    Type <span className="font-id">{request.requireTyping}</span> to continue
                                </Label>
                                <Input
                                    id="confirm-typing"
                                    value={typed}
                                    // The dialog exists to collect this one string; Radix would
                                    // otherwise focus Cancel and make everyone tab to the field.
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus
                                    autoComplete="off"
                                    onChange={event => setTyped(event.target.value)}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' && !blocked) {
                                            event.preventDefault()
                                            settle(true)
                                        }
                                    }}
                                />
                            </div>
                        )}

                        <AlertDialogFooter>
                            <AlertDialogCancel onClick={() => settle(false)}>
                                {request.cancelLabel ?? 'Cancel'}
                            </AlertDialogCancel>
                            <AlertDialogAction
                                variant={request.tone === 'destructive' ? 'destructive' : 'default'}
                                disabled={blocked}
                                onClick={() => settle(true)}>
                                {request.confirmLabel ?? 'Confirm'}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                )}
            </AlertDialog>
        </ConfirmContext>
    )
}
